import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormDescription,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { supabase } from '@/lib/supabase/supabaseClient'
import { errorToast } from '@/lib/utils/ToastUtils'
import { zodResolver } from '@hookform/resolvers/zod'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import ImageUpload from '@/components/ImageUpload'
import LoadingSpinner from '@/components/LoadingSpinner'
import { MdOutlineFileUpload, MdPerson } from 'react-icons/md'
import { store } from '@/lib/states/store'
import TimeZoneSelect from '@/components/timezoneSelect'
import { CalendarDays, Globe } from 'lucide-react'
import { SiGoogle } from 'react-icons/si'
import { trpcUtils, trpcVanilla } from '@/lib/trpc/vanilla'
import { useCoach } from '@/lib/states/session/hooks'
import { persistWeekStartsOn } from '@/lib/dates/updateWeekStart'
import { asWeekStartsOn } from '@/lib/dates/weekStart'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

export const Route = createFileRoute('/_auth/_dashboard/settings/_layout/')({
  component: AccountPage,
  loader: async ({ context }) => {
    const session = context.session
    if (!session) {
      throw new Error('User not found')
    }
    const userId = session.user.id
    const authProviders = session.user.app_metadata?.providers
    const isGoogleLinked = Array.isArray(authProviders)
      ? authProviders.includes('google')
      : session.user.app_metadata?.provider === 'google'

    const bootstrap = await trpcUtils.auth.getSession.ensureData()
    const coach = bootstrap.coach
    if (!coach) {
      throw new Error('User not found')
    }

    return {
      coach,
      userId,
      avatarUrl: coach.avatar?.url ?? '',
      isGoogleLinked,
    }
  },
})

function AccountPage() {
  const { coach, userId, avatarUrl, isGoogleLinked } = Route.useLoaderData()
  const sessionCoach = useCoach()
  const [loading, setLoading] = useState(false)
  const [savingWeekStart, setSavingWeekStart] = useState(false)
  const [avatar, setAvatar] = useState<string>(avatarUrl)
  const [avatarFile, setAvatarFile] = useState<File>()
  const weekStartsOn = asWeekStartsOn(sessionCoach?.weekStartsOn)

  const onAvatarChanged = (file: File) => {
    setAvatar(URL.createObjectURL(file))
    setAvatarFile(file)
  }

  async function onSubmit(data: AccountFormValues) {
    setLoading(true)
    const nextAvatarPath = coach.avatarPath ?? `${userId}/avatar.png`
    let nextAvatarVersion = coach.avatarVersion ?? 0

    if (avatarFile) {
      const { error: uploadError } = await supabase.storage
        .from('profile-images')
        .upload(nextAvatarPath, avatarFile, {
          cacheControl: '3600',
          upsert: true,
        })

      if (uploadError) {
        console.error('Error uploading avatar', uploadError)
        errorToast(uploadError)
        setLoading(false)
        return
      }

      nextAvatarVersion += 1
    }

    try {
      const updated = await trpcVanilla.auth.updateProfile.mutate({
        firstName: data.firstName,
        lastName: data.lastName,
        ...(avatarFile
          ? { avatarPath: nextAvatarPath, avatarVersion: nextAvatarVersion }
          : {}),
      })

      const { error: AuthError } = await supabase.auth.updateUser({
        data: {
          first_name: data.firstName,
          last_name: data.lastName,
        },
      })

      if (AuthError) {
        console.error('Error updating account', AuthError)
        toast.error('Error updating account')
        setLoading(false)
        return
      }

      if (avatarFile) {
        setAvatar(updated.avatar?.url ?? '')
        setAvatarFile(undefined)
      }

      const currentCoach = store.getState().session.coach
      if (currentCoach) {
        store.getState().session.setCoach({
          ...currentCoach,
          firstName: updated.firstName,
          lastName: updated.lastName,
          avatarPath: updated.avatarPath,
          avatarVersion: updated.avatarVersion,
          avatarUrl: updated.avatar?.url ?? currentCoach.avatarUrl ?? null,
        })
      }

      toast.success('Account updated successfully')
    } catch (error) {
      console.error('Error updating user', error)
      toast.error('Error updating account')
    } finally {
      setLoading(false)
    }
  }

  const accountFormSchema = z.object({
    firstName: z
      .string()
      .min(2, {
        message: 'Name must be at least 2 characters.',
      })
      .max(30, {
        message: 'Name must not be longer than 30 characters.',
      }),
    lastName: z
      .string()
      .min(2, {
        message: 'Name must be at least 2 characters.',
      })
      .max(30, {
        message: 'Name must not be longer than 30 characters.',
      }),
    email: z.string().email(),
  })

  type AccountFormValues = z.infer<typeof accountFormSchema>

  const defaultValues: Partial<AccountFormValues> = {
    firstName: coach.firstName,
    lastName: coach.lastName,
    email: coach.email ?? '',
  }

  const form = useForm<AccountFormValues>({
    resolver: zodResolver(accountFormSchema),
    defaultValues,
  })

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium">Account</h3>
        <p className="text-sm text-muted-foreground">
          Manage your account settings.
        </p>
      </div>
      <Separator />

      <div className="grid w-full max-w-sm items-center gap-2">
        <Label>Profile Picture</Label>
        <ImageUpload
          image={avatar}
          onImageFileChanged={onAvatarChanged}
          emptyView={
            <div className="flex flex-col items-center justify-center w-[200px] h-[200px] border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 hover:bg-gray-100 cursor-pointer">
              <MdPerson className="size-16 text-gray-400 mb-2" />
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <MdOutlineFileUpload className="size-4" />
                Upload Profile Picture
              </div>
            </div>
          }
        />
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>First Name</FormLabel>

                <FormControl>
                  <Input placeholder="First Name" type="text" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Last Name</FormLabel>

                <FormControl>
                  <Input placeholder="Last Name" type="text" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>

                {isGoogleLinked && (
                  <FormDescription className="flex items-center gap-2">
                    <SiGoogle className="h-4 w-4" />
                    Google sign-in
                  </FormDescription>
                )}
                <FormControl>
                  <Input
                    placeholder="Email"
                    type="email"
                    readOnly
                    disabled
                    {...field}
                  />
                </FormControl>

                <FormMessage />
              </FormItem>
            )}
          />
          <div className="space-y-2">
            <FormLabel className="flex items-center gap-2">
              <Globe className="h-4 w-4" />
              Timezone
            </FormLabel>
            <FormDescription>Timezone is set automatically.</FormDescription>
            <TimeZoneSelect
              value={coach.timezone ?? 'UTC'}
              onChange={() => {}}
              disabled
            />
          </div>
          <div className="space-y-2">
            <FormLabel className="flex items-center gap-2">
              <CalendarDays className="h-4 w-4" />
              Start of week
            </FormLabel>
            <FormDescription>
              Calendars and log weeks use this start day. It does not change
              programmed training days or a client&apos;s check-in day.
            </FormDescription>
            <Select
              value={String(weekStartsOn)}
              disabled={savingWeekStart}
              onValueChange={async (value) => {
                const next = asWeekStartsOn(Number(value))
                if (next === weekStartsOn) {
                  return
                }
                setSavingWeekStart(true)
                try {
                  const updated = await persistWeekStartsOn(next)
                  const currentCoach = store.getState().session.coach
                  if (currentCoach) {
                    store.getState().session.setCoach({
                      ...currentCoach,
                      weekStartsOn: updated.weekStartsOn,
                    })
                  }
                } catch (error) {
                  console.error('Error updating start of week', error)
                  toast.error('Error updating start of week')
                } finally {
                  setSavingWeekStart(false)
                }
              }}
            >
              <SelectTrigger className="max-w-sm">
                <SelectValue placeholder="Start of week" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Monday</SelectItem>
                <SelectItem value="0">Sunday</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button type="submit" className="w-32" disabled={loading}>
            Save {loading && <LoadingSpinner />}
          </Button>
        </form>
      </Form>
    </div>
  )
}
